// "Email these questions" — the Merlin's-questions digest (kanban bafe8216).
//
// Owner ask, 2026-09-28: "the leader in that department gets an email that
// says here are all the things outstanding and here is who they're assigned
// to, with a little outlink; and similar ones for everybody else that say
// your boss has seen this as well, here's one specific to you, here's a link
// to go answer the questions."
//
// One dialog, three parts, top to bottom: (1) who leads each team — the
// roadmap has no reporting structure, so an admin says it here once; (2)
// where each person's email goes — prefilled from their portal account, an
// admin can override; (3) the emails exactly as they would go out, one row
// per recipient, expandable to the rendered email, then a Send button.
// Nothing goes out until Send. "Send the emails" skips a recipient who already
// got this report's digest and retries one a run skipped or failed; each row
// also has its own Send / Resend (kanban 2e422cc2).

import { useCallback, useEffect, useMemo, useState } from 'react';
import EmailHtmlViewer from '../../components/portal/EmailHtmlViewer';
import {
  roadmapReportsApi,
  type DigestFrequency,
  type DigestPreview,
  type DigestPreviewResponse,
  type RoadmapPeopleResponse,
  type RoadmapPerson,
} from '../../services/roadmapReports';
import { digestRowState, ledgerFor, sendSummary } from './questionDigestUtils';
import styles from './QuestionDigestDialog.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

function errorMessage(err: unknown): string {
  const resp = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (typeof resp?.data?.detail === 'string') return resp.data.detail;
  if (resp?.status === 403) return 'Only an admin can do that.';
  return 'Something went wrong — try again.';
}

export function QuestionDigestDialog({ open, onClose }: Props) {
  const [people, setPeople] = useState<RoadmapPeopleResponse | null>(null);
  const [preview, setPreview] = useState<DigestPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sending, setSending] = useState<string | null>(null); // 'all' or a row key
  const [sent, setSent] = useState<string | null>(null);
  const [showSource, setShowSource] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, pv] = await Promise.all([roadmapReportsApi.listPeople(), roadmapReportsApi.previewDigests()]);
      setPeople(p);
      setPreview(pv);
      setDrafts(Object.fromEntries(p.people.map((x) => [x.name, x.email ?? ''])));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSent(null);
      void load();
    }
  }, [open, load]);

  const byName = useMemo(() => new Map((people?.people ?? []).map((p) => [p.name, p])), [people]);

  const savePerson = async (
    name: string,
    patch: Partial<{ email: string | null; leads: string[]; digest_frequency: DigestFrequency }>,
  ) => {
    const current = byName.get(name);
    if (!current) return;
    setSaving(name);
    setError(null);
    try {
      await roadmapReportsApi.setPerson(name, {
        email: patch.email !== undefined ? patch.email : current.email_source === 'directory' ? current.email : null,
        leads: patch.leads ?? current.leads,
        ...(patch.digest_frequency ? { digest_frequency: patch.digest_frequency } : {}),
      });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(null);
    }
  };

  const setLead = async (team: string, leadName: string) => {
    const currentLead = people?.teams.find((t) => t.team === team)?.lead ?? null;
    if (currentLead === (leadName || null)) return;
    // Release the current lead's hold on this team, then hand it to the new one.
    if (currentLead) {
      const row = byName.get(currentLead);
      if (row) await savePerson(currentLead, { leads: row.leads.filter((t) => t.toLowerCase() !== team.toLowerCase()) });
    }
    if (leadName) {
      const row = byName.get(leadName);
      if (row) await savePerson(leadName, { leads: [...row.leads.filter((t) => t.toLowerCase() !== team.toLowerCase()), team] });
    }
  };

  const commitEmail = (p: RoadmapPerson) => {
    const draft = (drafts[p.name] ?? '').trim();
    const shown = p.email ?? '';
    if (draft === shown) return;
    void savePerson(p.name, { email: draft || null });
  };

  const send = async (one?: DigestPreview) => {
    setSending(one ? `${one.kind}:${one.recipient_name}` : 'all');
    setError(null);
    try {
      const r = await roadmapReportsApi.sendDigests(one ? { kind: one.kind, recipient_name: one.recipient_name } : undefined);
      if (one) {
        const row = r.rows[0];
        setSent(
          row?.status === 'sent'
            ? `Sent to ${one.recipient_name}.`
            : `${one.recipient_name}: ${row?.status ?? 'nothing sent'}${row?.detail ? ` (${row.detail})` : ''}.`,
        );
      } else {
        setSent(`Sent ${r.sent}, skipped ${r.skipped}${r.failed ? `, failed ${r.failed}` : ''}${r.already ? `, ${r.already} already sent` : ''}.`);
      }
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSending(null);
    }
  };

  if (!open) return null;
  // People whose email frequency is off: the server never sends to them, so
  // their row says so and its Send button is not live.
  const offNames = new Set((people?.people ?? []).filter((p) => p.digest_frequency === 'off').map((p) => p.name));
  // The bulk button is live only while some row would actually send.
  const canSend =
    preview !== null &&
    preview.digests.some((d) => digestRowState(preview.already, d, offNames).label === 'will send') &&
    !sending &&
    !loading;

  return (
    <div className={styles.backdrop} role="presentation" onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label="Email Merlin's questions"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.head}>
          <div>
            <h2 className={styles.title}>Email Merlin&apos;s questions</h2>
            <p className={styles.how}>
              Each team lead gets everything outstanding for their team and who has it. Everyone named gets
              their own list. Both link back here to answer, typed or recorded, and people can also just reply
              to the email. Merlin asks the group who leads a team that has no lead yet.
              {preview?.report_date && <> Questions as of {preview.report_date}.</>}
            </p>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close"
            data-action="roadmap_question_digest_close"
            data-outcome="close"
            data-record-value="source"
            value="header"
          >
            ×
          </button>
        </header>

        {loading && <div className={styles.muted}>Loading…</div>}
        {error && <div className={styles.error} role="alert">{error}</div>}

        {people && (
          <section className={styles.section} aria-label="Team leads">
            <h3 className={styles.h3}>1 · Who leads each team</h3>
            {people.teams.length === 0 ? (
              <p className={styles.muted}>No teams on the active initiatives yet.</p>
            ) : (
              <ul className={styles.teams}>
                {people.teams.map((t) => (
                  <li key={t.team} className={styles.teamRow}>
                    <span className={styles.teamName}>{t.team}</span>
                    <span className={styles.teamCount}>{t.open_questions} open</span>
                    <select
                      aria-label={`Lead for ${t.team}`}
                      className={styles.select}
                      value={t.lead ?? ''}
                      disabled={saving !== null}
                      onChange={(e) => void setLead(t.team, e.target.value)}
                    >
                      <option value="">— no lead yet —</option>
                      {people.people.map((p) => (
                        <option key={p.name} value={p.name}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    {!t.lead && t.open_questions > 0 && (
                      <span className={styles.warn}>no lead digest for this team</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {people && (
          <section className={styles.section} aria-label="People">
            <h3 className={styles.h3}>2 · Where each person&apos;s email goes</h3>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Open</th>
                  <th>Email</th>
                  <th>From</th>
                  <th>How often</th>
                </tr>
              </thead>
              <tbody>
                {people.people.map((p) => (
                  <tr key={p.name} className={p.open_questions === 0 && p.leads.length === 0 ? styles.quiet : undefined}>
                    <td>
                      {p.name}
                      {p.leads.length > 0 && <span className={styles.leadTag}>lead · {p.leads.join(', ')}</span>}
                    </td>
                    <td>{p.open_questions}</td>
                    <td>
                      <input
                        aria-label={`Email for ${p.name}`}
                        className={`${styles.input} ${!p.email ? styles.inputMissing : ''}`}
                        type="email"
                        placeholder="no address — nothing is sent"
                        value={drafts[p.name] ?? ''}
                        disabled={saving === p.name}
                        onChange={(e) => setDrafts((d) => ({ ...d, [p.name]: e.target.value }))}
                        onBlur={() => commitEmail(p)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        }}
                      />
                    </td>
                    <td className={styles.source} title={p.set_basis ?? undefined}>
                      {p.email_source === 'directory' ? `set by ${p.set_by_email ?? 'an admin'}` : p.email_source === 'portal' ? 'portal account' : '—'}
                    </td>
                    <td>
                      <select
                        aria-label={`Digest frequency for ${p.name}`}
                        className={styles.select}
                        value={p.digest_frequency}
                        disabled={saving === p.name}
                        onChange={(e) => void savePerson(p.name, { digest_frequency: e.target.value as DigestFrequency })}
                      >
                        <option value="every_run">every run</option>
                        <option value="weekly">weekly</option>
                        <option value="off">off</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {preview && (
          <section className={styles.section} aria-label="Emails">
            <h3 className={styles.h3}>3 · The emails, as they would go out</h3>
            {preview.teams_without_lead.length > 0 && (
              <p className={styles.warnBlock}>
                No lead set for {preview.teams_without_lead.join(', ')} — those teams get no lead digest until one is
                chosen above.
              </p>
            )}
            {preview.digests.length === 0 ? (
              <p className={styles.muted}>Nothing outstanding — every question is answered or dismissed.</p>
            ) : (
              <ul className={styles.emails} data-testid="digest-list">
                {preview.digests.map((d) => {
                  const key = `${d.kind}:${d.recipient_name}`;
                  const done = ledgerFor(preview.already, d);
                  const isOpen = expanded === key;
                  const rowLabel = done?.status === 'sent' ? 'Resend' : 'Send';
                  const state = digestRowState(preview.already, d, offNames);
                  return (
                    <li key={key} className={styles.email}>
                      <div className={styles.emailRow}>
                        <button
                          type="button"
                          className={styles.emailHead}
                          aria-expanded={isOpen}
                          onClick={() => setExpanded(isOpen ? null : key)}
                        >
                          <span className={`${styles.kind} ${d.kind === 'lead' ? styles.kindLead : styles.kindPersonal}`}>
                            {d.kind === 'lead' ? 'lead' : 'personal'}
                          </span>
                          <span className={styles.to}>
                            {d.recipient_name}
                            <span className={styles.addr}>{d.recipient_email ?? 'no address'}</span>
                          </span>
                          <span className={styles.subject}>{d.subject}</span>
                          <span className={`${styles.state} ${styles[`state_${state.tone}`]}`}>{state.label}</span>
                        </button>
                        <button
                          type="button"
                          className={styles.rowSend}
                          aria-label={`${rowLabel} to ${d.recipient_name}`}
                          title={state.canSend ? `${rowLabel} this email to ${d.recipient_email} now` : state.note ?? undefined}
                          disabled={!state.canSend || sending !== null || loading}
                          onClick={() => void send(d)}
                        >
                          {sending === key ? 'Sending…' : rowLabel}
                        </button>
                      </div>
                      {state.note && <p className={styles.detail} data-testid="digest-note">{state.note}</p>}
                      {isOpen && (
                        <div className={styles.body}>
                          <button type="button" className={styles.sourceToggle} onClick={() => setShowSource((v) => !v)}>
                            {showSource ? 'Show the email' : 'Show the Markdown'}
                          </button>
                          {showSource ? (
                            <pre className={styles.source}>{d.body_markdown}</pre>
                          ) : (
                            <EmailHtmlViewer html={d.body_html} className={styles.rendered} />
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        <footer className={styles.foot}>
          <span className={styles.summary}>{sent ?? sendSummary(preview, offNames)}</span>
          <button
            type="button"
            className={styles.ghost}
            onClick={onClose}
            data-action="roadmap_question_digest_close"
            data-outcome="close"
            data-record-value="source"
            value="footer"
          >
            Close
          </button>
          <button type="button" className={styles.primary} onClick={() => void send()} disabled={!canSend}>
            {sending === 'all' ? 'Sending…' : 'Send the emails'}
          </button>
        </footer>
      </div>
    </div>
  );
}

/** The button that lives on the questions tab and owns the dialog. */
export function QuestionDigestButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(true)}
        title="Email each team lead the outstanding questions for their team, and each person their own list"
      >
        Email these questions…
      </button>
      <QuestionDigestDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
